import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { api, Resistance } from '../lib/api'
import { ResistanceCard } from '../components/ResistanceCard'
import { ResistanceForm } from '../components/ResistanceForm'
import { ExtensionBridge } from '../lib/extension'
import { supabase } from '../lib/supabase'
import { Plus, Settings } from 'lucide-react'

export function Dashboard() {
  const { user } = useAuth()
  const [resistances, setResistances] = useState<Resistance[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadResistances()
  }, [])

  const loadResistances = async () => {
    try {
      setLoading(true)
      console.log('Loading resistances...')
      const data = await api.getResistances()
      console.log('Resistances loaded:', data)
      setResistances(data)
    } catch (err) {
      console.error('Failed to load resistances:', err)
      setError('Failed to load resistances: ' + (err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateResistance = async (resistance: Omit<Resistance, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      console.log('Dashboard: Creating resistance:', resistance)
      setError(null) // Clear any previous errors
      const newResistance = await api.createResistance(resistance)
      console.log('Dashboard: Resistance created successfully:', newResistance)
      setResistances(prev => [...prev, newResistance])
      setShowForm(false)
      console.log('Dashboard: Form closed and resistance added to list')
      
      // Sync to extension
      ExtensionBridge.syncResistances().catch(err =>
        console.warn('Dashboard: Failed to sync resistance to extension:', err)
      )
    } catch (err) {
      console.error('Dashboard: Failed to create resistance:', err)
      setError('Failed to create resistance: ' + (err as Error).message)
    }
  }

  const handleUpdateResistance = async (id: string, updates: Partial<Resistance>) => {
    try {
      const updated = await api.updateResistance(id, updates)
      setResistances(prev => prev.map(r => r.id === id ? updated : r))
      
      // Sync to extension
      ExtensionBridge.syncResistances().catch(err =>
        console.warn('Dashboard: Failed to sync resistance update to extension:', err)
      )
    } catch (err) {
      setError('Failed to update resistance')
      console.error(err)
    }
  }

  const handleDeleteResistance = async (id: string) => {
    if (!confirm('Are you sure you want to delete this resistance?')) return
    
    try {
      await api.deleteResistance(id)
      setResistances(prev => prev.filter(r => r.id !== id))
      
      // Sync to extension
      ExtensionBridge.syncResistances().catch(err =>
        console.warn('Dashboard: Failed to sync resistance deletion to extension:', err)
      )
    } catch (err) {
      setError('Failed to delete resistance')
      console.error(err)
    }
  }


  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-sm text-red-600 hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Your Resistances</h2>
            <p className="mt-1 text-gray-600">
              Manage website restrictions to reduce compulsive usage
            </p>
          </div>
          
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Add Resistance</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading resistances...</p>
          </div>
        ) : resistances.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No resistances yet
            </h3>
            <p className="text-gray-600 mb-6">
              Create your first resistance to start reducing compulsive website usage.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First Resistance
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {resistances.map((resistance) => (
              <ResistanceCard
                key={resistance.id}
                resistance={resistance}
                onUpdate={(updates) => handleUpdateResistance(resistance.id, updates)}
                onDelete={() => handleDeleteResistance(resistance.id)}
              />
            ))}
          </div>
        )}

      {/* Resistance Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <ResistanceForm
              onSubmit={handleCreateResistance}
              onCancel={() => setShowForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  )
}